import useVesselsStore from '@/store/vessels';
import { Vessel } from '@/types/api';
import { Combobox, Transition } from '@headlessui/react';
import { Fragment, useEffect, useState } from 'react';

import CheckIcon from '@/assets/icons/check.svg';
import CaretDownIcon from '@/assets/icons/caret-down.svg';

import moment from 'moment';
import BaseDateRangePickerPopOver from '@/components/ui/daterange';
import { Search, Ship } from 'lucide-react';

type Props = {
  onSearch: Function;
  type?: string;
  withVessel?: boolean;
};

export default function FormSearch({ onSearch, type, withVessel = true }: Props) {
  /***
   * Get vessels data
   */
  const vesselStore = useVesselsStore((state) => state);
  const { vessels } = vesselStore;

  /***
   * Selected vessels
   */
  const [selected, setSelected] = useState<Vessel | null>(null);
  const [query, setQuery] = useState('');

  /***
   * Date range
   */
  const [dateRange, setDateRange] = useState<{
    start: string;
    end: string;
  }>({
    start: moment().subtract(7, 'days').toString(),
    end: moment().toString(),
  });

  const filteredVessels =
    query === ''
      ? vessels.filter((val) => {
          if (type === 'rpm' || type === 'hourmeter') {
            return val.device_type === 'RPM';
          }
          return val;
        })
      : vessels
          .filter((val) =>
            val.vessel_name
              .toLowerCase()
              .replace(/\s+/g, '')
              .includes(query.toLowerCase().replace(/\s+/g, '')),
          )
          .filter((val) => {
            if (type === 'rpm' || type === 'hourmeter') {
              return val.device_type === 'RPM';
            }
            return val;
          });

  const onButtonSearch = () => {
    onSearch({
      vessel: selected?.id || null,
      startDate: moment(dateRange.start).format('YYYY-MM-DD HH:mm:ss'),
      endDate: moment(dateRange.end).format('YYYY-MM-DD HH:mm:ss'),
    });
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-4 relative mb-6">
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-brand-500 rounded-l-2xl" />
      <div className={`grid grid-cols-1 ${withVessel ? 'md:grid-cols-[1.4fr_1.2fr_auto]' : 'md:grid-cols-[1fr_auto]'} gap-3 items-end pl-2`}>
        {withVessel && (
          <div className="block">
            <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Vessel</span>
            <Combobox value={selected} onChange={setSelected}>
              {({ open }) => {
                return (
                  <div className="relative">
                    <Transition
                      show={open}
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                      afterLeave={() => setQuery('')}
                    >
                      <Combobox.Options
                        className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm transform translate-y-120 bottom-auto top-full dark:bg-gray-700 dark:text-white"
                        style={{ zIndex: 999 }}
                      >
                        {filteredVessels?.length === 0 && query !== '' ? (
                          <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                            Nothing found.
                          </div>
                        ) : (
                          <>
                            <Combobox.Option
                              key={'all'}
                              className={({ active }) =>
                                `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                  active
                                    ? 'bg-brand-500 text-white dark:text-white'
                                    : 'text-gray-900 dark:text-white'
                                }`
                              }
                              value={'All'}
                            >
                              {({ selected, active }) => (
                                <>
                                  <span
                                    className={`block truncate ${
                                      selected ? 'font-medium' : 'font-normal'
                                    }`}
                                  >
                                    All
                                  </span>
                                  {selected && (
                                    <span
                                      className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                        active
                                          ? 'text-white dark:text-white'
                                          : 'text-brand-500 dark:text-white'
                                      }`}
                                    >
                                      <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                    </span>
                                  )}
                                </>
                              )}
                            </Combobox.Option>
                            {filteredVessels?.map((vessel: Vessel, key) => (
                              <Combobox.Option
                                key={key}
                                className={({ active }) =>
                                  `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                    active ? 'bg-brand-500 text-white' : 'text-gray-900 dark:text-white'
                                  }`
                                }
                                value={vessel}
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span
                                      className={`block truncate ${
                                        selected ? 'font-medium' : 'font-normal'
                                      }`}
                                    >
                                      {vessel?.vessel_name}
                                    </span>
                                    {selected ? (
                                      <span
                                        className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                          active ? 'text-white' : 'text-brand-500'
                                        }`}
                                      >
                                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                      </span>
                                    ) : null}
                                  </>
                                )}
                              </Combobox.Option>
                            ))}
                          </>
                        )}
                      </Combobox.Options>
                    </Transition>

                    <div className="relative w-full cursor-default overflow-hidden text-left focus:outline-none focus-visible:ring-2 sm:text-sm">
                      <Combobox.Button as="div" className="w-full relative">
                        <Ship className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                        <Combobox.Input
                          className="h-10 w-full pl-9 pr-10 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/40 relative z-0"
                          displayValue={(vessel: Vessel) => vessel?.vessel_name}
                          onChange={(event) => setQuery(event.target.value)}
                          placeholder="Search Vessel Name"
                          autoComplete="off"
                        />
                      </Combobox.Button>
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <CaretDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </Combobox.Button>
                    </div>
                  </div>
                );
              }}
            </Combobox>
          </div>
        )}

        <div className="block">
          <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Date Range</span>
          <BaseDateRangePickerPopOver
            value={dateRange}
            setValue={setDateRange}
            className="w-full h-10 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40"
          />
        </div>

        <button
          type="button"
          onClick={onButtonSearch}
          className="h-10 w-full md:w-auto px-6 rounded-lg bg-brand-500 text-white text-sm font-semibold hover:opacity-95 shadow-sm inline-flex items-center justify-center gap-2 transition-all"
        >
          <Search className="h-4 w-4" /> Search
        </button>
      </div>
    </div>
  );
}
